'use client';

import * as Popover from '@radix-ui/react-popover';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { productSelectionAtom } from '~/features/ProductSelection';
import LogoButton from '~/shared/assets/brand/logo-button.svg';
import { useSessionAdmin } from '~/shared/lib/auth';
import { useTooltip } from '../model/hooks';
import { AdminProductSelectionActions } from './AdminProductSelectionActions';

export default function AdminTooltip() {
    const { isAdmin, isPending: isAdminPending } = useSessionAdmin();
    const productSelection = useAtomValue(productSelectionAtom);
    const tooltip = useTooltip(isAdmin);

    const [userOpen, setUserOpen] = useState(false);

    const open = useMemo(() => userOpen || productSelection.length > 0, [userOpen, productSelection.length]);

    const setOpen = useCallback((next: boolean | ((p: boolean) => boolean)) => {
        setUserOpen(next);
    }, []);

    useEffect(() => {
        if (productSelection.length > 0) setUserOpen(true);
    }, [productSelection.length]);

    if (isAdminPending) return null;
    if (!isAdmin) return null;
    if (tooltip.type === 'product') return null;

    const allowed = tooltip.type === 'homepage' || tooltip.type === 'list' || tooltip.type === 'category';
    if (!allowed) return null;

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger
                className='fixed z-[100] flex h-12 w-12 items-center justify-center rounded-full bg-black shadow-lg outline-none duration-100'
                style={{ bottom: 'calc(var(--page-inset) * 1.5)', right: 'calc(var(--page-inset) * 1.5)' }}
            >
                {productSelection.length === 0 && <LogoButton className='h-[80%] w-[80%] rounded-full' />}
                {productSelection.length > 0 && (
                    <span className='text-20 font-500 text-neutral-50'>{productSelection.length}</span>
                )}
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    className="flex flex-col-reverse bg-transparent outline-none data-[state='open']:animate-in data-[state='closed']:animate-out !z-[50]"
                    side='top'
                    sideOffset={8}
                >
                    <AdminProductSelectionActions open={open} setOpen={setOpen} />
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
